import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@monksflow/monks-ui';
import { SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AiPromptPanel } from '@/features/audiences/ui/AiPromptPanel';
import { IMAGE_MODEL_OPTIONS, VIDEO_MODEL_OPTIONS } from '@/features/audiences/model';
import PlanImage from '@/shared/assets/images/Plan.png';
import { SuccessBanner } from '@/shared/ui/SuccessBanner';
import { useProfileMediaTab } from '../tabs/ProfileMediaTab/hooks/useProfileMediaTab';
import { ProfileImageSectionV2, VideoSectionV2 } from '../mediaV2';

export function ProfileMediaTabV2() {
  const { t } = useTranslation('audiences');
  const media = useProfileMediaTab();

  return (
    <>
      <div className="flex flex-col gap-md">
        <ProfileImageSectionV2
          photoAssetId={media.photoAssetId}
          isProcessing={media.isProcessingImage}
          processingLabel={media.processingLabel}
          disabled={media.isProcessingMedia}
          onUploadClick={media.handleUploadClick}
          onRegenerateClick={media.handleImageRegenerateClick}
          metadata={media.imageMetadata}
        />

        {media.showBanner && media.bannerType && (
          <SuccessBanner
            title={
              media.bannerType === 'upload'
                ? t('audiences.profileMediaTab.imageUploadedSuccessfully')
                : t('audiences.profileMediaTab.imageGeneratedSuccessfully')
            }
            description={t('audiences.profileMediaTab.wouldYouLikeToAutomatically')}
            onClose={media.handleBannerClose}
            actions={
              <>
                <Button onClick={media.handleBannerGenerateVideos}>
                  <SparklesIcon className="size-4" />
                  {t('audiences.profileMediaTab.generateVideos')}
                </Button>
                <Button variant="outline" onClick={media.handleBannerClose}>
                  {t('audiences.profileMediaTab.donTGenerate')}
                </Button>
              </>
            }
          />
        )}

        <Separator />

        <VideoSectionV2
          thumbnailVideoAssetId={media.mouseOverAssetId}
          detailVideoAssetId={media.videoAssetId}
          isProcessing={media.isProcessingMedia}
          processingLabel={media.processingLabel}
          onRegenerateClick={media.handleVideoRegenerateClick}
          onGenerateClick={media.handleVideoGenerateClick}
          thumbnailMetadata={media.thumbnailMetadata}
          detailMetadata={media.detailMetadata}
          thumbnailFailed={media.thumbnailFailed}
          detailFailed={media.detailFailed}
          thumbnailErrorMessage={media.thumbnailErrorMessage}
          detailErrorMessage={media.detailErrorMessage}
          retryingVideoType={media.retryingVideoType}
          onRetryVideo={(type) => void media.handleRetryVideo(type)}
        />
      </div>

      {/* Upload runs off a hidden input the section's button clicks for it. */}
      <input
        ref={media.fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={(e) => void media.handleFileChange(e)}
      />

      <Dialog open={media.imageModalOpen} onOpenChange={media.setImageModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('audiences.profileMediaTab.reGenerateImage')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <AiPromptPanel
              description={t('audiences.profileMediaTab.describeThePhysicalTraitsYou')}
              placeholder={t('audiences.profileMediaTab.describeYourPersonaImage')}
              agentName={t('audiences.profileMediaTab.imageGenerator')}
              agentThumbnailUrl={PlanImage}
              promptValue={media.imagePrompt}
              onPromptChange={media.setImagePrompt}
              onSend={() => void media.handleImageSend()}
              onClose={() => media.setImageModalOpen(false)}
              selectedModel={media.selectedImageModel}
              onModelChange={media.setSelectedImageModel}
              isGenerating={media.isRegeneratingImage}
              modelOptions={IMAGE_MODEL_OPTIONS}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={media.videoModalOpen} onOpenChange={media.setVideoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {media.videoAssetId || media.mouseOverAssetId
                ? t('audiences.profileMediaTab.reGenerateVideos')
                : t('audiences.profileMediaTab.generateVideos')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <AiPromptPanel
              description={t('audiences.profileMediaTab.addSpecificInstructionsForThe')}
              placeholder={t('audiences.profileMediaTab.describeYourPersonaVideos')}
              agentName={t('audiences.profileMediaTab.videoGenerator')}
              agentThumbnailUrl={PlanImage}
              promptValue={media.videoPrompt}
              onPromptChange={media.setVideoPrompt}
              onSend={() => void media.handleVideoSend()}
              onClose={() => media.setVideoModalOpen(false)}
              selectedModel={media.selectedVideoModel}
              onModelChange={media.setSelectedVideoModel}
              isGenerating={media.isGeneratingVideos}
              modelOptions={VIDEO_MODEL_OPTIONS}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

ProfileMediaTabV2.displayName = 'ProfileMediaTabV2';
